import Notification from '@rio-cloud/rio-uikit/Notification';
import Select from "@rio-cloud/rio-uikit/components/selects/Select";
import Button from "@rio-cloud/rio-uikit/Button";
import { Scheduling } from "../../../../../models/schedule";
import { Occurrence } from "../../../../../models/occurrence";
import useMediaQuery from "@mui/material/useMediaQuery";
import VehicleIcon from "../../../../VehicleIcon";
import { useIntl } from "react-intl";
import { getMaintenanceGroupLabel, getMaintenanceGroupLabelByValue, MaintenanceGroupEnum } from "../../../../maintenanceGroupSidebar/components/enums/maintenanceGroupEnum";
import { editMaintenanceGroup } from "../../../../../api/fetchMaintenanceGroup";
import { useOccurrenceVehicleSummaryCard } from "../hooks/useOccurrenceVehicleSummaryCard";
import CopyButton from "./CopyButton";
import OccurrenceVehicleScheduledServicesDialog from "./occurrenceVehicleScheduledServicesDialog";

interface IOccurrenceVehicleSummaryCardProps {
  scheduling?: Scheduling | null;
  occurrence?: Occurrence | null;
  allowMaintenanceGroupChange?: boolean;
  refetchData?: () => void;
}

const OccurrenceVehicleSummaryCard = (props: IOccurrenceVehicleSummaryCardProps) => {
  const { scheduling, occurrence, allowMaintenanceGroupChange, refetchData } = props;

  const intl = useIntl();
  const isMobile = useMediaQuery("(max-width: 767px)");

  const { 
    maintenanceGroup,
    maintenanceGroupLoaded,
    customMaintenanceGroup,
    customMaintenanceGroupLoading,
    customMaintenanceGroupSaved,
    volksEssentialsPlan,
    isScheduledServicesDialogOpen,
    setCustomMaintenanceGroup,
    setCustomMaintenanceGroupLoading,
    setCustomMaintenanceGroupSaved,
    openScheduledServicesDialog,
    closeScheduledServicesDialog,
  } = useOccurrenceVehicleSummaryCard(scheduling?.asset?.chassis || occurrence?.vehicleChassis || '');

  if (!scheduling && !occurrence) {
    return null;
  }

  const vehicleData = {
    name: scheduling?.asset?.name || occurrence?.vehicleName || "Veículo",
    licensePlate: scheduling?.asset?.licensePlate || occurrence?.vehicleLicensePlate || "-",
    chassis: scheduling?.asset?.chassis || occurrence?.vehicleChassis || "-",
    odometer: scheduling?.asset?.odometer || scheduling?.odometer || occurrence?.vehicleOdometer || "-",
    hourmeter: scheduling?.asset?.hourmeter || scheduling?.hourmeter || occurrence?.vehicleHourmeter || "-",
    model: scheduling?.asset?.model || occurrence?.model || occurrence?.vehicleModel || "",
    year: scheduling?.asset?.year || occurrence?.vehicleYear || null,
    contractNumber: scheduling?.asset?.contractNumber || occurrence?.contractNumber,
  };

  const hasVehicleInfo = vehicleData.name !== "Veículo" || vehicleData.chassis !== "-" || vehicleData.licensePlate !== "-";

  return (
    <div className="margin-bottom-20">
      <div className="text-size-h5 font-bold margin-bottom-10">
        {scheduling ? intl.formatMessage({ id: "intl-msg:kanban.form.occurrenceVehicleSummaryCard.schedulingSummary" }) : intl.formatMessage({ id: "intl-msg:kanban.form.occurrenceVehicleSummaryCard.vehicleInfo" })}
      </div>

      <div className="p-4 bg-lighter rounded margin-bottom-15">
        <div className="flex gap-4">
          {scheduling && (
            <div className="margin-bottom-15 w-1/2">
              <h6 className="text-size-16 text-color-black font-bold margin-bottom-5">
                {scheduling?.scheduleNumber || "-"}
              </h6>
              <p className="text-color-status-resting text-size-14 m-0">
                {intl.formatMessage({ id: "intl-msg:schedulingSummary.date" })}: {scheduling?.date ? new Date(scheduling.date).toLocaleDateString(intl.locale) : "-"}
                {scheduling?.dealership?.name && (
                  <>
                    <br />
                    {intl.formatMessage({ id: "intl-msg:schedulingSummary.dealership" })}: {scheduling.dealership.name}
                  </>
                )}
              </p>
            </div>
          )}

          {/* Informações da occurrence quando não há scheduling */}
          {!scheduling && occurrence && (
            <div className="margin-bottom-15 w-1/2">
              <h6 className="text-size-16 text-color-black font-bold margin-bottom-5">
                {occurrence.protocolNumber || ""}
              </h6>
              <p className="text-color-status-resting text-size-14 m-0">
                {intl.formatMessage({ id: "intl-msg:schedulingSummary.customer" })}: {occurrence.customer || ""}
                {occurrence.dealershipName && (
                  <>
                    <br />
                    {intl.formatMessage({ id: "intl-msg:schedulingSummary.dealership" })}: {occurrence.dealershipName}
                  </>
                )}
              </p>
            </div>
          )}

          {allowMaintenanceGroupChange && (!maintenanceGroup && !!vehicleData.model && maintenanceGroupLoaded) ?
            <div className="flex gap-4 ">
              <div className={`form-group form-group-sm`}>
                <label htmlFor="maintenanceGroup" className="truncate text-size-12">
                  {intl.formatMessage({ id: "intl-msg:occurrence.maintenanceGroup" })}
                </label>
                <Select
                  id="maintenanceGroup"
                  bsSize="sm"
                  options={[
                    { id: '1', label: getMaintenanceGroupLabel(MaintenanceGroupEnum.RODOVIARIO) },
                    { id: '2', label: getMaintenanceGroupLabel(MaintenanceGroupEnum.MISTO) },
                    { id: '3', label: getMaintenanceGroupLabel(MaintenanceGroupEnum.SEVERO) },
                    { id: '4', label: getMaintenanceGroupLabel(MaintenanceGroupEnum.ESPECIAL) },
                  ]}
                  placeholder={intl.formatMessage({ id: "intl-msg:occurrence.maintenanceGroup" })}
                  value={[customMaintenanceGroup ?? ""]}
                  onChange={(e) => setCustomMaintenanceGroup(e?.id ?? null)}
                  disabled={customMaintenanceGroupSaved}
                />
              </div>
              {!customMaintenanceGroupSaved && <div className="flex items-start mt-[27px]">
                <Button
                  bsSize="sm"
                  bsStyle={Button.PRIMARY}
                  disabled={customMaintenanceGroupLoading}
                  onClick={async () => {
                    await editMaintenanceGroup(occurrence?.vehicleChassis || '', customMaintenanceGroup || '');
                    setCustomMaintenanceGroupLoading(true);
                    setTimeout(() => {
                      refetchData?.()
                      setCustomMaintenanceGroupLoading(false);
                      setCustomMaintenanceGroupSaved(true);
                    }, 2000)
                  }}
                >
                  {customMaintenanceGroupLoading ? intl.formatMessage({ id: "intl-msg:occurrence.savingMaintenanceGroup" }) : intl.formatMessage({ id: "intl-msg:occurrence.saveMaintenanceGroup" })}
                </Button>
              </div>}
            </div> : null
          }
        </div>

        {/* Informações do veículo usando dados consolidados */}
        {hasVehicleInfo && (
          <div className={`flex items-start gap-4 ${isMobile ? "flex-col gap-3 items-center" : ""}`}>
            <VehicleIcon
              chassis={vehicleData.chassis ?? ''}
              size={80}
            />

            <div className="flex-1 padding-left-10">
              <p className="text-color-black text-size-16 font-medium m-0">
                {vehicleData.name} | {intl.formatMessage({ id: "intl-msg:schedulingSummary.licensePlate" })}: {vehicleData.licensePlate}
              </p>
              <p className="text-color-status-resting text-size-14 m-0">
                {intl.formatMessage({ id: "intl-msg:schedulingSummary.chassis" })} - {vehicleData.chassis} {maintenanceGroupLoaded ? `| ${intl.formatMessage({ id: "intl-msg:schedulingSummary.maintenanceGroup" })}: ${(customMaintenanceGroup || maintenanceGroup) ? getMaintenanceGroupLabelByValue(customMaintenanceGroup ?? maintenanceGroup ?? '') : intl.formatMessage({ id: "intl-msg:kanban.form.occurrenceVehicleSummaryCard.notDefined" })}` : ''}
                <br />

                {intl.formatMessage({ id: "intl-msg:schedulingSummary.odometer" })}: {vehicleData.odometer !== "-" ? `${vehicleData.odometer} km` : "-"} | {intl.formatMessage({ id: "intl-msg:schedulingSummary.hourmeter" })}: {vehicleData.hourmeter !== "-" ? `${vehicleData.hourmeter}` : "-"}

                {vehicleData.model && (<><br />{intl.formatMessage({ id: "intl-msg:schedulingSummary.model" })}: {vehicleData.model}</>)}
              </p>
              {vehicleData.contractNumber && (
                <div className="flex items-center gap-2 margin-top-5">
                  <span className="text-color-primary font-bold text-size-14">
                    {intl.formatMessage({ id: "intl-msg:kanban.form.occurrenceVehicleSummaryCard.contract" }, { contractNumber: vehicleData.contractNumber })}
                  </span>
                  <CopyButton textToCopy={vehicleData.contractNumber} />
                </div>
              )}
              <p className="text-color-status-resting text-size-14 m-0">
                {volksEssentialsPlan &&
                  <div className="flex gap-6">
                    <div>{intl.formatMessage({ id: "intl-msg:schedulingSummary.contractType" })}: Volks|Total Essencial</div>
                    {allowMaintenanceGroupChange && customMaintenanceGroupSaved &&
                      <div
                        className="text-color-primary text-size-14 m-0 cursor-pointer"
                        onClick={() => {
                          refetchData?.()
                          Notification.info(intl.formatMessage({ id: "intl-msg:schedulingSummary.loadingContractData" }))
                          setTimeout(() => {
                            Notification.success(intl.formatMessage({ id: "intl-msg:schedulingSummary.contractDataUpdated" }))
                          }, 2000)
                        }}
                      ><span className="underline">{intl.formatMessage({ id: "intl-msg:schedulingSummary.reloadContractData" })}</span> <span className="rioglyph rioglyph-repeat" />
                    </div>}
                  </div>
                }
              </p>
            </div>

            {scheduling?.date && (
              <div className="margin-bottom-15">
                <h6 className="text-size-16 text-color-black font-bold margin-bottom-5">
                  {intl.formatMessage({ id: "intl-msg:schedulingSummary.scheduleDate" })}
                </h6>
                <p className="text-color-status-resting text-size-14 m-0">
                  {new Date(scheduling.date).toLocaleDateString(intl.locale)}
                </p>
              </div>
            ) }

            {/* Tags de planos */}
            {((scheduling?.asset?.plans?.length ?? 0) > 0) && (
              <div className="flex gap-6 margin-bottom-20">
                <span className="px-2 py-1 green-tag">RIO</span>
                <span className="px-2 py-1 green-tag">{intl.formatMessage({ id: "intl-msg:kanban.form.occurrenceVehicleSummaryCard.underWarranty" })}</span>
                {(scheduling?.asset?.volksTotalPlan) && (
                  <span className="px-2 py-1 bg-white dark:bg-neutral-800 white-tag">
                    Volkstotal {scheduling?.asset?.volksTotalPlan}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Campanhas de Campo */}
      {scheduling?.campaigns && scheduling.campaigns.length > 0 && (
        <div className="revision-itens margin-bottom-15">
          <h6 className="text-size-16 text-color-black font-bold margin-bottom-10">
            {intl.formatMessage({ id: "intl-msg:schedulingSummary.fieldCampaigns" })}
          </h6>
          <div className="p-4 bg-white dark:bg-neutral-800 border rounded">
            <ul className="space-y-5 text-size-14 text-color-black m-0 padding-left-10">
              {scheduling.campaigns.map((campaign: any, index: number) => (
                <li key={index} className="padding-bottom-5">
                  <div className="text-bold">{campaign.name || campaign.number}</div>
                  {campaign.description && (
                    <div className="text-color-status-resting padding-left-10">
                      {campaign.description}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {((scheduling?.checkup?.services?.length ?? 0) > 0 ||
        (scheduling?.checkup?.parts?.length ?? 0) > 0 ||
        (scheduling?.otherServices?.length ?? 0) > 0 ||
        !!(scheduling?.observation || scheduling?.comments)) && (
        <div className="revision-itens">
          <h6 className="text-size-16 text-color-black font-bold margin-bottom-10">
            {intl.formatMessage({ id: "intl-msg:schedulingSummary.scheduledServicesAndParts" })}
          </h6>
          <div className="p-4 bg-white dark:bg-neutral-800 border rounded">
            <Button variant={Button.VARIANT_LINK} className="padding-0" onClick={openScheduledServicesDialog}>
              {intl.formatMessage({ id: "intl-msg:schedulingSummary.viewServiceDetails" })}
            </Button>
          </div>
        </div>
      )}

      <OccurrenceVehicleScheduledServicesDialog
        show={isScheduledServicesDialogOpen}
        scheduling={scheduling}
        onClose={closeScheduledServicesDialog}
      />
    </div>
  );
};

export default OccurrenceVehicleSummaryCard;
